/**
 * New Year quote prints — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const newYearQuotePrintsPack: ToilePack = {
    slug: 'new-year-quote-prints',
    category: 'season',
    name: 'New Year quote prints',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'line', label: 'Fresh start', subject: 'A vertical print lettered "New year, new page" in a serif with a scatter of stars and nothing else'},
        {key: 'begin', label: 'One word', subject: 'A vertical print with the single word "BEGIN" lettered large and a small sunburst beneath'},
        {key: 'motto', label: 'Year motto', subject: 'A vertical print lettered "Cheers to what\'s next" in script inside a ring of confetti'},
    ],
    styles: [
        {key: 'deco', label: 'Art deco', style: 'art deco illustration, geometric fans and sunbursts, clean lines, no outlines', palette: 'black and gold with a little cream', framing: 'Centered on a solid black background with an even margin'},
        {key: 'neon', label: 'Neon party', style: 'neon party graphic, glowing lines, confetti and streamers', palette: 'electric pink, cyan and gold on deep navy', framing: 'Centered on a dark background, built to read clean at small size'},
        {key: 'minimal', label: 'Minimal', style: 'modern minimal illustration, few flat shapes, generous space', palette: 'gold and white with a single black', framing: 'Centered on a plain white background with generous space around it'},
    ],
    directions: ['for an office wall', 'in a single word only', 'as a two-line couplet'],
    directionHint: 'try: for an office wall',
    seo: {
        title: 'New Year Quote Prints: Fresh Start, Begin, Motto',
        metaDescription: 'Nine new year quote prints — a fresh-start line, a one-word begin, a year motto — quiet typographic wall art, three styles.',
        h1: 'New year quote prints: fresh start, begin, motto',
        tagline: 'Nine prints, three midnight subjects, three styles each.',
        intro: 'Quiet, typographic wall art for the frame: a fresh-start line, a one-word begin, a year motto, each set in clean lettering with room to breathe. Ask for your own quote, a specific year, or a different tone, and the same three styles carry it.',
        useCases: [
            'a framed print for the home office',
            "a New Year's gift for a friend",
            'a bedroom or entryway wall print',
            'a journal or planner cover page',
        ],
        faq: [
            {q: 'What does this pack actually look like?', a: 'Nine typographic prints — a fresh-start line, a one-word begin, a year motto — quiet and lettered, in three styles.'},
            {q: 'Can I use my own quote instead?', a: 'The pack renders the specific lines shown; supplying a different quote is done through the direction field, which the AI carries across the three styles.'},
            {q: 'What size should I print these at?', a: 'Each renders as a single square image at print resolution — standard frame sizes like 8x8 or 12x12 inches work well cropped or matted.'},
            {q: 'Is this frameable wall art or a digital-only design?', a: "It's a printable design — download it and print through any home or professional print service for a physical frame."},
            {q: 'Can I sell prints made from this design?', a: 'Yes — check the license terms on your account for the specifics of commercial use.'},
        ],
    },
}
